/**
 * Monta o item de menu "Alterar técnico" para o BoardOrderContextMenu.
 * Exibe técnicos visíveis e, ao passar o cursor no técnico, as opções:
 * PREVISTO PARA HOJE, PREVISTAS PARA AMANHÃ, FUTURA.
 *
 * @param {{
 *   visibleTechnicianNames?: string[],
 *   areasWithTechnicians?: Array<{
 *     id: string | number,
 *     name: string,
 *     technicians: string[],
 *   }>,
 *   orderIds: string[],
 *   fromTechnician: string,
 *   fromGroup: string,
 *   onMoveOrder: function,
 *   onOpenFutureModal: function,
 *   disabled?: boolean,
 *   menuId?: string,
 *   menuLabel?: string,
 *   menuIcon?: string,
 *   menuIconNode?: import('react').ReactNode,
 * }} params
 * @returns {{ id: string, label: string, icon?: string, iconNode?: import('react').ReactNode, subItems: Array, disabled?: boolean }}
 */
export function getAlterarTecnicoMenuItem({
  visibleTechnicianNames,
  areasWithTechnicians,
  orderIds,
  fromTechnician,
  fromGroup,
  onMoveOrder,
  onOpenFutureModal,
  disabled = false,
  menuId = 'alterar-tecnico',
  menuLabel = 'Alterar técnico',
  menuIcon = 'bi bi-person-gear',
  menuIconNode = null,
}) {
  const ids = Array.isArray(orderIds) && orderIds.length > 0 ? orderIds : [];
  const sections = [
    { id: 'previsto-hoje', label: 'PREVISTO PARA HOJE', groupName: 'Previsto para hoje' },
    { id: 'previstas-amanha', label: 'PREVISTAS PARA AMANHÃ', groupName: 'Previstas para amanhã' },
    { id: 'futura', label: 'FUTURA', groupName: 'FUTURA' },
  ];

  const normalizedFromTechnician = (fromTechnician || '').trim().toLowerCase();

  // Quando áreas com técnicos são fornecidas, montar menu em 3 níveis:
  // Alterar técnico → Área → Técnico → Sessão
  let subItems;

  if (Array.isArray(areasWithTechnicians) && areasWithTechnicians.length > 0) {
    const areaItems = areasWithTechnicians
      .map((area) => {
        const technicians = (area.technicians || []).filter((techName) => {
          if (!techName) return false;
          const normalizedTech = techName.trim().toLowerCase();
          return normalizedTech && normalizedTech !== normalizedFromTechnician;
        });

        if (technicians.length === 0) return null;

        return {
          id: `area-${area.id}`,
          label: area.name,
          subItems: technicians.map((techName) => ({
            id: `tech-${area.id}-${techName}`,
            label: techName,
            subItems: sections.map((s) => ({
              id: `${s.id}-${area.id}-${techName}`,
              label: s.label,
              onClick: () => {
                if (s.groupName === 'FUTURA') {
                  onOpenFutureModal({
                    technicianName: techName,
                    orderIds: ids,
                    fromGroup: fromGroup || 'Previsto para hoje',
                    fromTechnicianName: fromTechnician,
                  });
                } else {
                  onMoveOrder(
                    ids,
                    fromTechnician,
                    fromGroup || 'Previsto para hoje',
                    techName,
                    s.groupName
                  );
                }
              },
            })),
          })),
        };
      })
      .filter(Boolean);

    subItems = areaItems;
  } else {
    // Fallback: comportamento antigo (Alterar técnico → Técnico → Sessão)
    const technicianSubItems = (visibleTechnicianNames || []).map((techName) => ({
      id: `tech-${techName}`,
      label: techName,
      subItems: sections.map((s) => ({
        id: `${s.id}-${techName}`,
        label: s.label,
        onClick: () => {
          if (s.groupName === 'FUTURA') {
            onOpenFutureModal({
              technicianName: techName,
              orderIds: ids,
              fromGroup: fromGroup || 'Previsto para hoje',
              fromTechnicianName: fromTechnician,
            });
          } else {
            onMoveOrder(ids, fromTechnician, fromGroup || 'Previsto para hoje', techName, s.groupName);
          }
        },
      })),
    }));

    subItems = technicianSubItems;
  }

  return {
    id: menuId,
    label: menuLabel,
    ...(menuIconNode != null ? { iconNode: menuIconNode } : { icon: menuIcon }),
    subItems,
    disabled,
  };
}
